import {
  AmplifyHub, AnalyticsService,
  AnalyticsTrackingEvent,
  BrandclubStoreConfigUtils, buildAppInstallationInfo, IAppInstallationInfo, initializeAmplifyAuth, Logger, useFingerprintJSContext
} from "@brandclub/common-ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import {
  appConfigQueryOptions, useAppConfig, useBrandAppConfigWithFallback
} from "./api/cache/appConfigQueryOptions";
import { appThemeQueryOptions } from "./api/cache/appThemeQueryOptions";
import {
  activeCartsQueryOptions,
  useCartCount
} from "./api/cache/cartQueryOptions";
import {
  brandclubProductQueryOptions,
  useCurrentBrandclubProduct,
  useDtcSiteProduct
} from "./api/cache/dtcProductQueryOptions";
import { usePersistedQueryManager } from "./api/cache/PersistedQueryManager";
import { updateAppInstallationInfoInCheckoutBar } from "./api/rest/user";
import { useCurrentStacklineSku } from "./hooks/useCurrentStacklineSku";
import { useTrackActions, useTrackViewEvent } from "./hooks/useTracking";
import {
  userProfileQueryOptions,
  useUserProfile
} from "./hooks/useUserProfile";
import { useAppDispatch } from "./redux/hooks";
import { updateVisitorTracking } from "./redux/reducers/visitorTracking";
import { removeFunctionFields } from "./utils";
import { HOST_DOMAIN } from "./utils/domain";


const logger = new Logger("AppInitializer");

const initializeMixpanel = (domain: string) => {
  try {
    if (!AnalyticsService.isMixpanelInitialized()) {
      AnalyticsService.initializeMixpanel(domain);
      logger.debug("Mixpanel initialized early");
    }
  } catch (e) {
    logger.error("Early Mixpanel initialization failed", e);
  }
};

const initAppInstallationInfo = async (
  userId: string | undefined,
  signal?: AbortSignal
) => {
  if (!userId) {
    return;
  }
  try {
    const appId = BrandclubStoreConfigUtils.getWebAppId();
    const appInstallationInfo: IAppInstallationInfo =
      await buildAppInstallationInfo({
        userId,
        appId,
      });
    if (appInstallationInfo && !signal?.aborted) {
      await updateAppInstallationInfoInCheckoutBar(appInstallationInfo, signal);
    }
  } catch (e) {
    // Only log non-abort errors
    if (e instanceof Error && (e.name === 'AbortError' || e.message.includes('abort'))) {
      logger.debug("App installation info initialization was aborted");
      return;
    }
    logger.error("An error occurred when building appInstallationInfo", e);
  }
};

// Combined tracking component
const MixpanelTracking: React.FC<{ isAuthReady: boolean }> = ({ isAuthReady }) => {
  const dispatch = useAppDispatch();
  const visitorTracking = useFingerprintJSContext();
  const currentStacklineSku = useCurrentStacklineSku();
  const { data: cartCount } = useCartCount();

  // Only call useUserProfile after auth is initialized
  const { data: userProfile, isLoading: isUserProfileLoading } = useUserProfile({
    enabled: isAuthReady,
  });
  const isLoggedIn = !!userProfile;
  const appInstallationControllerRef = useRef<AbortController | null>(null);

  // Get product data from cache without triggering a fetch
  const { data: product } = useQuery({
    ...brandclubProductQueryOptions(currentStacklineSku),
    enabled: false,
    staleTime: Infinity,
  });

  // Get checkout bar treatments from cache without triggering a fetch
  const { data: checkoutBarTreatments } = useQuery({
    ...appConfigQueryOptions(),
    enabled: false,
    staleTime: Infinity,
    select: (data) => data.domainConfig?.abTestingTreatmentResults,
  });

  const trackPageView = useTrackViewEvent(AnalyticsTrackingEvent.PAGE_VIEW);
  const [_, appendCheckoutBarActionDetails] = useTrackActions();

  // Update Mixpanel identity when user data is available
  useEffect(() => {
    if (!isAuthReady || isUserProfileLoading) {
      return;
    }

    try {
      AnalyticsService.setCurrentVisitorTracking(visitorTracking);
      AnalyticsService.setMixpanelUserIdentity(visitorTracking, userProfile);
      if (visitorTracking?.uniqueVisitorId) {
        dispatch(updateVisitorTracking(removeFunctionFields(visitorTracking)));
      }
    } catch (e) {
      logger.error("Error updating Mixpanel identity", e);
    }
  }, [dispatch, isAuthReady, isUserProfileLoading, visitorTracking, userProfile]);

  // Track page views - but only after we know auth status
  useEffect(() => {
    if (!isAuthReady) {
      return;
    }

    const checkoutBarVisible =
      checkoutBarTreatments?.["brandclub_dtc_checkout_bar"]?.treatment !== "off" &&
        (currentStacklineSku || !!cartCount) ? true : undefined;
    const checkoutView = checkoutBarVisible ? "checkout_bar" : "inpage_checkout";
    const productInfo = product ? {
      pageType: "Catalog" as const,
      pageId: product.stacklineSku,
      retailerId: product.retailerId,
      retailPrice: product.retailPrice,
      retailerSku: product.retailerSku,
      stacklineSku: product.stacklineSku,
      totalRewardAmount: product.totalRewardAmount,
      retailerProductPageUrl: product.retailerProductPageUrl,
      title: product.title,
      checkoutEligibility: product.checkoutEligibility,
      matchedProducts: product.matchedProducts,
    } : {
    };

    trackPageView(
      {
        ...productInfo,
        checkoutView,
        checkoutBarTreatments,
        isAuthenticated: isLoggedIn,
      },
      {}
    );

    appendCheckoutBarActionDetails({
      ...productInfo,
      isAuthenticated: isLoggedIn,
    });
  }, [currentStacklineSku, isLoggedIn, product, isAuthReady, cartCount, checkoutBarTreatments]);

  // Handle app installation info with proper abort control
  useEffect(() => {
    if (!isAuthReady || isUserProfileLoading || !userProfile?.userId) {
      return;
    }

    // Abort any previous initialization
    appInstallationControllerRef.current?.abort("New initialization");

    const controller = new AbortController();
    appInstallationControllerRef.current = controller;

    initAppInstallationInfo(userProfile.userId, controller.signal);

    return () => {
      controller.abort("Component unmount");
      if (appInstallationControllerRef.current === controller) {
        appInstallationControllerRef.current = null;
      }
    };
  }, [userProfile?.userId, isAuthReady]);

  return null;
};

export const AppInitializer: React.FC<PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient();
  const persistedQueryManager = usePersistedQueryManager();
  const [isAuthReady, setIsAuthReady] = useState(false);
  const initStartedRef = useRef({
    auth: false,
    amplifyHub: false,
  });
  // Initialize Mixpanel immediately
  useEffect(() => {
    initializeMixpanel(HOST_DOMAIN);
  }, []);

  // Load core configurations
  const { data: brandAppConfig } = useBrandAppConfigWithFallback({
    notifyOnChangeProps: [],
  });
  const { data: appConfig } = useAppConfig();

  const { refetch: fetchUserProfile } = useUserProfile({
    enabled: false, // we don't want to fetch the user profile until auth is ready
  });

  // Initialize auth when config is ready
  useEffect(() => {
    if (!appConfig?.authConfig || initStartedRef.current.auth) {
      return;
    }
    initStartedRef.current.auth = true;
    const initialize = async () => {
      try {
        // Initialize auth
        await initializeAmplifyAuth(appConfig.authConfig, appConfig?.domainConfig?.domainName);
        logger.debug("Amplify auth initialized successfully");
        await fetchUserProfile().catch(() => {
          logger.debug("User not logged in");
        });
      } catch (error) {
        logger.error('Auth initialization failed', error);
      }
    };

    initialize().then(() => {
      setIsAuthReady(true);
    });
  }, [appConfig]);

  // Set up auth event listeners
  useEffect(() => {
    if (initStartedRef.current.amplifyHub) {
      return;
    }
    initStartedRef.current.amplifyHub = true;
    const unsubscribe = AmplifyHub.listen("auth", ({ payload: { event, data } }) => {
      logger.debug("Amplify auth event received", event, data);
      if (event === "signOut") {
        logger.debug("User signed out removing user profile from cache");
        persistedQueryManager.invalidateQueries({
          queryKey: userProfileQueryOptions().queryKey,
          type: "all",
        });
        window.location.reload();
      } else if (["signIn", "signIn_Complete", "signUp"].includes(event)) {
        logger.debug("User signed in fetching user profile");
        fetchUserProfile();
      }
    });
    logger.debug("Subscribed to Amplify auth events");
    return () => {
      logger.debug("Unsubscribing from Amplify auth events");
      unsubscribe();
      initStartedRef.current.amplifyHub = false;
    };
  }, [queryClient]);

  // Enable data queries as soon as configs are loaded (parallel to auth)
  const canLoadData = !!(brandAppConfig && appConfig);

  useQuery({
    ...appThemeQueryOptions(),
    notifyOnChangeProps: [],
    enabled: canLoadData,
  });

  useCurrentBrandclubProduct({
    notifyOnChangeProps: [],
    enabled: canLoadData,
  });

  useDtcSiteProduct({
    notifyOnChangeProps: [],
    enabled: canLoadData,
  });

  useQuery({
    ...activeCartsQueryOptions(),
    notifyOnChangeProps: [],
    enabled: canLoadData && isAuthReady,
  });

  // Handle page refresh
  useEffect(() => {
    if (!canLoadData) {
      return;
    }

    const navigation = performance.getEntriesByType(
      "navigation"
    )[0] as PerformanceNavigationTiming;
    if (navigation?.type === "reload") {
      persistedQueryManager.invalidateQueries({
        queryKey: activeCartsQueryOptions().queryKey,
        type: "all",
      });
    }
  }, [persistedQueryManager, canLoadData]);

  // Don't render until configs are loaded
  if (!canLoadData) {
    return null;
  }

  return (
    <>
      <MixpanelTracking isAuthReady={isAuthReady} />
      {children}
    </>
  );
};
